import React, { useState, useEffect } from "react";
import { Scale } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { getUserLabels } from "../services/labelService";
import Pagination, { ITEMS_PER_PAGE } from "../components/Pagination";
import PageHeader from "../components/PageHeader";

function TodayCases() {
  const { globalUser } = useAuth();
  const [todayCases, setTodayCases] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [labels, setLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const getLabelById = (labelId) => {
    return labels.find((label) => label.id === labelId);
  };

  // Get today's date in DD/MM/YYYY format
  const getTodayString = () => {
    const today = new Date();
    return `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
  };

  useEffect(() => {
    const fetchLabels = async () => {
      if (!globalUser?.uid) return;
      try {
        const userLabels = await getUserLabels(globalUser.uid);
        setLabels(userLabels);
      } catch (error) {
        console.error("Error fetching labels:", error);
      }
    };
    fetchLabels();
  }, [globalUser]);

  useEffect(() => {
    const fetchCases = async () => {
      if (!globalUser) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const todayStr = getTodayString();
      let caseList = [];

      const caseStatus = ["pending", "disposed"];

      for (const status of caseStatus) {
        const caseCollection = collection(db, status);
        const caseQuery = query(caseCollection, where("owner", "==", globalUser.uid));
        const caseSnapshot = await getDocs(caseQuery);
        caseSnapshot.forEach((doc) => {
          const caseData = doc.data();
          if (caseData.nextHearingDate === todayStr) {
            caseList.push({
              id: doc.id,
              caseNumber: caseData.caseNo,
              type: caseData.caseType,
              petitionerName: caseData.petitionerName,
              court: caseData.courtName,
              status: caseData.status || "Status not available",
              nextHearingDate: caseData.nextHearingDate || null,
              labels: caseData.labels || [],
              labelsByUser: caseData.labelsByUser || {},
              collection: status,
            });
          }
        });
      }

      setTodayCases(caseList);
      setIsLoading(false);
    };

    fetchCases();
  }, [globalUser]);

  return (
    <div className="min-h-screen bg-[#0c0a29] text-white relative">
      <Navbar />
      <div className="background-circle circle-3 z-0"></div>
      <div className="background-circle circle-4 z-0"></div>

      <main className="px-4 sm:px-6 md:px-8 py-8">
        <PageHeader
          title="Today's Cases"
          subtitle="Cases with a hearing today"
        />

        <div className="flex flex-col items-center">
          <div className="flex items-center justify-center gap-6 mb-4 mt-1 w-11/12 md:w-1/2">
            {isLoading ? (
              <div className="bg-[#2d2a5a] rounded-lg p-6 text-center w-96 animate-pulse">
                <div className="h-8 bg-[#3d3a6a] rounded w-3/4 mx-auto mb-2"></div>
                <div className="h-10 bg-[#3d3a6a] rounded w-1/2 mx-auto"></div>
              </div>
            ) : (
              <div className="bg-yellow-300 bg-opacity-60 rounded-lg p-6 hover:bg-opacity-80 transition cursor-pointer text-center w-96">
                <h3 className="text-black text-2xl mb-2">TODAY'S HEARINGS</h3>
                <p className="text-black text-3xl font-bold">
                  {todayCases.length}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="md:px-12">
          {isLoading && (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-[#1e1b4b] rounded-lg p-4 animate-pulse h-[200px]">
                  <div className="flex h-full">
                    <div className="mr-4 flex-shrink-0">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#2d2a5a] rounded-md"></div>
                    </div>
                    <div className="flex-grow flex flex-col gap-2">
                      <div className="h-4 bg-[#2d2a5a] rounded w-3/4"></div>
                      <div className="h-6 bg-[#2d2a5a] rounded w-full"></div>
                      <div className="h-3 bg-[#2d2a5a] rounded w-1/2"></div>
                      <div className="h-3 bg-[#2d2a5a] rounded w-2/3"></div>
                      <div className="h-3 bg-[#2d2a5a] rounded w-1/2"></div>
                      <div className="mt-auto h-4 bg-[#2d2a5a] rounded w-1/3"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && todayCases.length === 0 && (
            <div className="text-center py-10 text-gray-400">
              <Scale size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-xl">No cases with hearing today.</p>
            </div>
          )}

          {!isLoading && todayCases.length > 0 && (
            <>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {todayCases.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((caseItem) => {
                  const userLabelIds = caseItem.labelsByUser?.[globalUser?.uid] || caseItem.labels || [];
                  const hasLabels = userLabelIds && userLabelIds.length > 0;

                  return (
                    <div key={caseItem.id} className="relative rounded-lg transition-all duration-200 h-full min-h-[200px]">
                      <Link
                        to={`/portal/case/${caseItem.id}?from=${caseItem.collection}`}
                        className="block bg-[#1e1b4b] rounded-lg hover:bg-[#2d2a5a] transition-colors duration-200 h-full group pt-8 pb-4 px-4"
                      >
                        {/* Label Badges */}
                        {hasLabels && (
                          <div className="absolute top-2 right-2 left-2 z-10">
                            <div className="flex items-start justify-end gap-2 flex-wrap">
                              <div className="flex flex-wrap gap-1 justify-end">
                                {userLabelIds.slice(0, 2).map((labelId) => {
                                  const label = getLabelById(labelId);
                                  if (!label) return null;
                                  return (
                                    <div
                                      key={labelId}
                                      className="px-2 py-0.5 rounded-full text-white text-xs font-medium shadow-sm"
                                      style={{ backgroundColor: label.color }}
                                    >
                                      {label.name}
                                    </div>
                                  );
                                })}
                                {userLabelIds.length > 2 && (
                                  <div className="px-2 py-0.5 rounded-full bg-gray-600 text-white text-xs font-medium shadow-sm">
                                    +{userLabelIds.length - 2}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="flex h-full">
                          <div className="mr-4 flex-shrink-0 flex items-start">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#2d2a5a] rounded-md">
                              <Scale size={32} color="#FFD700" strokeWidth={1.5} />
                            </div>
                          </div>
                          <div className="flex-grow overflow-hidden flex flex-col">
                            <p className="text-sm text-gray-400 truncate">
                              Case Number: {caseItem.caseNumber}
                            </p>
                            <h3 className="text-xl font-bold text-white truncate">{caseItem.type}</h3>
                            <p className="text-sm text-gray-300 truncate">Party Name: {caseItem.petitionerName}</p>
                            <p className="text-sm text-gray-300 truncate">Court: {caseItem.court}</p>
                            <p className="text-sm text-gray-300 truncate">
                              Next Hearing Date: {caseItem.nextHearingDate}
                            </p>
                            <p className={`text-sm font-semibold truncate ${
                              caseItem.status?.toLowerCase() === "disposed" ? "text-red-400" : "text-green-400"
                            }`}>
                              Status: {caseItem.status}
                            </p>
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
              <Pagination
                currentPage={currentPage}
                totalItems={todayCases.length}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default TodayCases;
